package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.DesignAssetDto
import dev.lingvo.shared.DesignAssetKind
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.WorkspaceAssetDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.XhrUpload
import dev.lingvo.site.api.deleteWorkspaceScreenshotAsset
import dev.lingvo.site.api.importWorkspaceScreenshotAsset
import dev.lingvo.site.api.uploadWorkspaceScreenshotAsset
import dev.lingvo.site.api.workspaceScreenshotAssets
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LDisclosure
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.w3c.files.get
import kotlin.js.Date
import kotlin.random.Random

/** Imports are explicit so workspace bytes use the existing project-scoped renderer and quotas. */
@Composable
internal fun WorkspaceAssetLibrary(
    data: DesignPageData, runner: ScreenshotsActionRunner, enabled: Boolean,
    onImported: (DesignAssetDto) -> Unit,
) {
    val subject = liveProjectRoute(data.project.slug)?.authoritySubject(Session.organizations)
    var opened by remember(subject) { mutableStateOf(false) }
    var entries by remember(subject) { mutableStateOf<List<WorkspaceAssetDto>>(emptyList()) }
    var reload by remember(subject) { mutableStateOf(0) }
    var kind by remember(subject) { mutableStateOf(DesignAssetKind.BACKGROUND) }
    var busy by remember(subject) { mutableStateOf(false) }
    var failure by remember(subject) { mutableStateOf<String?>(null) }
    var notice by remember(subject) { mutableStateOf<String?>(null) }
    var pending by remember(subject) { mutableStateOf<WorkspaceAssetDto?>(null) }
    var progress by remember(subject) { mutableStateOf<BackgroundUploadProgress?>(null) }
    val uploader = remember(subject) { XhrUpload() }
    DisposableEffect(uploader) { onDispose { uploader.abort() } }
    val wrap = Modifier.styleModifier { property("flex-wrap", "wrap"); property("min-width", "0") }
    LaunchedEffect(opened, subject, reload) {
        if (!opened || subject == null) return@LaunchedEffect
        try {
            entries = requestProjectResource(subject.action, data.project.slug) {
                ApiClient.workspaceScreenshotAssets(data.project.slug, data.plan.platform)
            }
            failure = null
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) { failure = tr("app.projects.slug.screenshots.workspace_asset_library.couldnt_load_the_workspace_library_retry", "Couldn't load the workspace library. Retry.") }
    }
    LDisclosure(t("app.projects.slug.screenshots.workspace_asset_library.workspace_asset_library", "Workspace asset library"), opened, { opened = it }) {
        Column(Modifier.gap(Space.MD).styleModifier { property("min-width", "0"); property("max-width", "100%") }) {
            LMuted(t("app.projects.slug.screenshots.workspace_asset_library.logos_backgrounds_and_device_artwork_png", "Logos, backgrounds and device artwork. PNG or JPEG up to 8 MiB; screenshot storage limits apply. Importing makes project artwork available in Canvas Assets."))
            if (pending == null) failure?.let { LAlert(it, live = true) }
            Div(attrs = { attr("role", "status"); attr("aria-live", "polite"); attr("aria-atomic", "true") }) {
                notice?.let { LMuted(it) }
            }
            LButton(t("app.projects.slug.screenshots.workspace_asset_library.reload_library", "Reload library"), onClick = { reload++ }, enabled = !busy, variant = LButtonVariant.GHOST)
            if (enabled) {
                LSelect(kind.name, DesignAssetKind.entries.map { LSelectOption(it.name, assetKindLabel(it)) },
                    { value -> kind = DesignAssetKind.valueOf(value) }, label = t("app.projects.slug.screenshots.workspace_asset_library.artwork_kind", "Artwork kind"), enabled = !busy)
                Input(InputType.File, attrs = {
                    attr("accept", "image/png,image/jpeg")
                    attr("aria-label", tr("app.projects.slug.screenshots.workspace_asset_library.upload_workspace_artwork", "Upload workspace artwork"))
                    if (busy) attr("disabled", "")
                    style { property("min-height", "40px"); property("max-width", "100%") }
                    onChange { event ->
                        val file = event.target.files?.get(0)
                        event.target.value = ""
                        if (file != null && !busy) {
                            notice = null
                            failure = when {
                                file.size.toDouble() > ScreenshotSpecs.MAX_IMAGE_BYTES -> tr("app.projects.slug.screenshots.workspace_asset_library.choose_an_image_up_to_8", "Choose an image up to 8 MiB.")
                                file.type !in setOf("image/png", "image/jpeg") -> tr("app.projects.slug.screenshots.workspace_asset_library.choose_a_png_or_jpeg_image", "Choose a PNG or JPEG image.")
                                else -> null
                            }
                            if (failure == null) {
                                val id = libraryAssetId()
                                runner.run(ScreenshotsPrivilege.DESIGN, { busy = it }, {
                                    ApiClient.uploadWorkspaceScreenshotAsset(data.project.slug, data.plan.platform, id, kind, file, file.type, uploader) { done, total ->
                                        progress = BackgroundUploadProgress(done, total)
                                    }
                                }, { failure = libraryFailure(); progress = null }, {
                                    progress = null; reload++; notice = tr("app.projects.slug.screenshots.workspace_asset_library.artwork_is_in_the_workspace_library", "Artwork is in the workspace library.")
                                })
                            }
                        }
                    }
                })
            }
            if (busy && progress != null) LTaskProgress(
                done = progress!!.sent.coerceAtMost(Int.MAX_VALUE.toLong()).toInt(),
                total = progress!!.total.coerceAtMost(Int.MAX_VALUE.toLong()).toInt(),
                label = t("app.projects.slug.screenshots.workspace_asset_library.uploading_artwork", "Uploading artwork"), indeterminate = progress!!.total <= 0,
            )
            if (entries.isEmpty()) LMuted(t("app.projects.slug.screenshots.workspace_asset_library.no_workspace_artwork_loaded_yet", "No workspace artwork loaded yet."))
            entries.forEach { asset ->
                Column(Modifier.gap(Space.SM).styleModifier { property("overflow-wrap", "anywhere") }) {
                    LMuted(tf("screenshots.library.entry", "{id} · {kind} · {width} × {height}", "id" to asset.id,
                        "kind" to assetKindLabel(asset.kind), "width" to asset.width, "height" to asset.height))
                    if (enabled) Row(wrap.gap(Space.SM)) {
                        LButton(t("app.projects.slug.screenshots.workspace_asset_library.use_in_this_design", "Use in this design"), enabled = !busy, variant = LButtonVariant.SECONDARY,
                            accessibleLabel = tf("screenshots.library.use_named_artwork", "Use in this design: {id}", "id" to asset.id),
                            onClick = {
                                failure = null; notice = null
                                runner.run(ScreenshotsPrivilege.DESIGN, { busy = it }, {
                                    ApiClient.importWorkspaceScreenshotAsset(data.project.slug, data.plan.platform, asset.id, libraryAssetId())
                                }, { failure = libraryFailure() }, { imported ->
                                    onImported(imported); notice = tr("app.projects.slug.screenshots.workspace_asset_library.artwork_imported_canvas_assets_also_lists", "Artwork imported. Canvas Assets also lists it for adding image layers.")
                                })
                            })
                        LButton(t("app.projects.slug.screenshots.workspace_asset_library.delete_from_library", "Delete from library"), enabled = !busy, variant = LButtonVariant.GHOST,
                            accessibleLabel = tf("screenshots.library.delete_named_artwork", "Delete from library: {id}", "id" to asset.id),
                            onClick = { pending = asset; failure = null; notice = null })
                    }
                }
            }
        }
    }
    LConfirmDialog(pending != null, title = t("app.projects.slug.screenshots.workspace_asset_library.delete_workspace_artwork", "Delete workspace artwork?"),
        confirmLabel = t("app.projects.slug.screenshots.workspace_asset_library.delete_from_library", "Delete from library"), tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.projects.slug.screenshots.workspace_asset_library.workspace_library_removal", "Workspace library removal"),
        consequence = tf("screenshots.library.delete_named_consequence", "Removes artwork {id} from the workspace library. Deletion is refused while a current design, saved template or named snapshot uses it.",
            "id" to pending?.id.orEmpty()),
        busy = busy, error = failure, onCancel = { if (!busy) pending = null }, onConfirm = {
            val asset = pending
            if (asset != null && enabled && !busy) runner.run(ScreenshotsPrivilege.DESIGN, { busy = it }, {
                ApiClient.deleteWorkspaceScreenshotAsset(data.project.slug, data.plan.platform, asset.id)
            }, { failure = tr("app.projects.slug.screenshots.workspace_asset_library.couldnt_delete_artwork_it_may_be", "Couldn't delete artwork. It may be used by a design, template or named snapshot. Reload the library and retry.") }, {
                pending = null; reload++
                notice = trf("screenshots.library.deleted_named_artwork", "Artwork {id} was deleted from the library.", "id" to asset.id)
            })
        })
}

internal fun assetKindLabel(kind: DesignAssetKind): String = when (kind) {
    DesignAssetKind.BACKGROUND -> tr("app.projects.slug.screenshots.workspace_asset_library.background", "Background")
    DesignAssetKind.LOGO -> tr("app.projects.slug.screenshots.workspace_asset_library.logo", "Logo")
    DesignAssetKind.DEVICE_ART -> tr("app.projects.slug.screenshots.workspace_asset_library.device_artwork", "Device artwork")
}

private fun libraryAssetId(): String = "art-${Date.now().toLong()}-${Random.nextInt(1_000_000)}"
private fun libraryFailure(): String = tr("app.projects.slug.screenshots.workspace_asset_library.couldnt_use_the_workspace_artwork_check", "Couldn't use the workspace artwork. Check image format, storage limits and project access, then retry.")
